import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CardMedia, Typography, Box, IconButton, Slider, Stack } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import CloseIcon from '@mui/icons-material/Close';
import Hls from 'hls.js';
import { IProject } from '../../../types/global/helper';
import { useThemeContext } from '../../../hook/useThemeManager';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { theme } from '../../../constants/theme';

export interface DemoReelCardProps {
    project: IProject;
    isEditMode?: boolean;
    onDelete?: (project: IProject) => void;
    /** Opens the global act / vertical player (was triggered by play). Prefer `onExpandToPlayer`. */
    onClickPlay?: (project: IProject) => void;
    /** Fullscreen / expand: open app player only (not inline). */
    onExpandToPlayer?: (project: IProject) => void;
    /** First meaningful inline play — e.g. view-count / analytics (optional). */
    onInlinePlaybackStart?: (project: IProject) => void;
    /** Full-width 16:9 showcase (artist work profile). */
    variant?: 'default' | 'artistFeatured';
}

function getPlayableProjectSrc(project: IProject): { url: string; isHls: boolean } | null {
    if (project.hls) {
        return { url: project.hls, isHls: true };
    }
    const r = project.resolutions;
    if (!r) {
        return null;
    }
    const order = ['1080p', '720p', '480p', '360p'] as const;
    for (const key of order) {
        if (r[key]) {
            return { url: r[key], isHls: false };
        }
    }
    return null;
}

function projectSupportsInlineVideo(project: IProject): boolean {
    if (project.media_type && project.media_type !== 'video') {
        return false;
    }
    return getPlayableProjectSrc(project) !== null;
}

function formatMediaTime(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds < 0) {
        return '0:00';
    }
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
}

const DemoReelCard: React.FC<DemoReelCardProps> = ({
    project,
    isEditMode = false,
    onDelete,
    onClickPlay,
    onExpandToPlayer,
    onInlinePlaybackStart,
    variant = 'default',
}) => {
    const { themeStyle } = useThemeContext();
    const featured = variant === 'artistFeatured';
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const hlsRef = useRef<Hls | null>(null);
    const inlineStartedRef = useRef(false);

    const [hovered, setHovered] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [scrubValue, setScrubValue] = useState<number | null>(null);
    const [volumeUi, setVolumeUi] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const isScrubbingRef = useRef(false);

    const r = project.resolutions;
    const streamKey = [
        project._id,
        project.hls ?? '',
        project.media_type ?? '',
        r?.['1080p'] ?? '',
        r?.['720p'] ?? '',
        r?.['480p'] ?? '',
        r?.['360p'] ?? '',
    ].join('|');

    const playable = getPlayableProjectSrc(project);
    const canPlayInline = projectSupportsInlineVideo(project) && !!playable;
    const expandHandler = onExpandToPlayer ?? onClickPlay;

    useEffect(() => {
        inlineStartedRef.current = false;
        setHasStarted(false);
        setIsPlaying(false);
        setHovered(false);
        setCurrentTime(0);
        setDuration(0);
        setScrubValue(null);
        setVolumeUi(1);
        setIsMuted(false);
    }, [project._id]);

    useEffect(() => {
        const video = videoRef.current;
        const source = getPlayableProjectSrc(project);
        const inlineOk = projectSupportsInlineVideo(project) && !!source;
        if (!video || !source || !inlineOk) {
            return;
        }

        const { url, isHls } = source;

        if (isHls) {
            if (video.canPlayType('application/vnd.apple.mpegurl')) {
                video.src = url;
            } else if (Hls.isSupported()) {
                const hls = new Hls({ enableWorker: true });
                hlsRef.current = hls;
                hls.loadSource(url);
                hls.attachMedia(video);
            }
        } else {
            video.src = url;
        }

        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
            if (video) {
                video.pause();
                video.removeAttribute('src');
                video.load();
            }
        };
    }, [streamKey]); // eslint-disable-line react-hooks/exhaustive-deps -- streamKey tracks URL identity

    useEffect(() => {
        const v = videoRef.current;
        if (!v || !canPlayInline) {
            return;
        }
        const onTime = () => {
            if (!isScrubbingRef.current) {
                setCurrentTime(v.currentTime);
            }
        };
        const onDuration = () => {
            const d = v.duration;
            setDuration(Number.isFinite(d) ? d : 0);
        };
        const onVol = () => {
            setIsMuted(v.muted);
            setVolumeUi(v.volume);
        };
        v.addEventListener('timeupdate', onTime);
        v.addEventListener('loadedmetadata', onDuration);
        v.addEventListener('durationchange', onDuration);
        v.addEventListener('volumechange', onVol);
        onDuration();
        onVol();
        return () => {
            v.removeEventListener('timeupdate', onTime);
            v.removeEventListener('loadedmetadata', onDuration);
            v.removeEventListener('durationchange', onDuration);
            v.removeEventListener('volumechange', onVol);
        };
    }, [streamKey, canPlayInline]);

    const notifyInlineStart = useCallback(() => {
        if (inlineStartedRef.current) {
            return;
        }
        inlineStartedRef.current = true;
        onInlinePlaybackStart?.(project);
    }, [onInlinePlaybackStart, project]);

    const handleVideoPlay = useCallback(() => {
        setIsPlaying(true);
        setHasStarted(true);
    }, []);

    const handleVideoPause = useCallback(() => {
        setIsPlaying(false);
    }, []);

    const handleVideoEnded = useCallback(() => {
        setIsPlaying(false);
    }, []);

    const handleCenterPlay = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            if (!canPlayInline) {
                expandHandler?.(project);
                return;
            }
            const v = videoRef.current;
            if (!v) {
                return;
            }
            notifyInlineStart();
            v.play().catch(() => {
                expandHandler?.(project);
            });
        },
        [canPlayInline, expandHandler, notifyInlineStart, project],
    );

    const handleVideoSurfaceClick = useCallback(() => {
        if (!canPlayInline || !hasStarted) {
            return;
        }
        const v = videoRef.current;
        if (!v) {
            return;
        }
        if (v.paused) {
            notifyInlineStart();
            v.play().catch(() => {});
        } else {
            v.pause();
        }
    }, [canPlayInline, hasStarted, notifyInlineStart]);

    const handleTogglePause = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            const v = videoRef.current;
            if (!v) {
                return;
            }
            if (v.paused) {
                notifyInlineStart();
                v.play().catch(() => {});
            } else {
                v.pause();
            }
        },
        [notifyInlineStart],
    );

    const handleExpand = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            videoRef.current?.pause();
            expandHandler?.(project);
        },
        [expandHandler, project],
    );

    const handleScrubChange = useCallback((_: Event, value: number | number[]) => {
        isScrubbingRef.current = true;
        setScrubValue(typeof value === 'number' ? value : value[0]);
    }, []);

    const handleScrubCommit = useCallback((_: Event | React.SyntheticEvent, value: number | number[]) => {
        const t = typeof value === 'number' ? value : value[0];
        const v = videoRef.current;
        if (v && Number.isFinite(t)) {
            v.currentTime = t;
            setCurrentTime(t);
        }
        isScrubbingRef.current = false;
        setScrubValue(null);
    }, []);

    const handleVolumeChange = useCallback((_: Event, value: number | number[]) => {
        const pct = typeof value === 'number' ? value : value[0];
        const v = videoRef.current;
        if (v) {
            const next = pct / 100;
            v.volume = next;
            v.muted = next === 0;
            setVolumeUi(next);
            setIsMuted(next === 0);
        }
    }, []);

    const handleMuteToggle = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        const v = videoRef.current;
        if (!v) {
            return;
        }
        const nextMuted = !v.muted;
        v.muted = nextMuted;
        if (!nextMuted && v.volume === 0) {
            v.volume = 0.8;
        }
        setIsMuted(v.muted);
        setVolumeUi(v.volume);
    }, []);

    const progressMax = Math.max(duration, 0.01);
    const progressDisplay = scrubValue ?? currentTime;
    const volumeSliderValue = (isMuted ? 0 : volumeUi) * 100;

    const showCenterPlay =
        hovered && ((!canPlayInline && !!expandHandler) || (canPlayInline && !isPlaying));
    const showControlBar = canPlayInline || !!expandHandler;
    const playCircleSize = featured ? 72 : 56;
    const iconOnHighlight = theme[themeStyle].pure_black;

    return (
        <Box
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            sx={{
                position: 'relative',
                ...(featured
                    ? {
                          width: '100%',
                          maxWidth: '100%',
                          aspectRatio: '16/9',
                          minHeight: { xs: 220, sm: 300, md: 360 },
                          borderRadius: '16px',
                      }
                    : {
                          width: '100%',
                          maxWidth: '100%',
                          aspectRatio: '16/9',
                          minHeight: { xs: 200, sm: 220 },
                          borderRadius: '16px',
                      }),
                overflow: 'hidden',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
                bgcolor: '#1a1a1a',
                border: '1px solid rgba(255,255,255,0.06)',
                mb: 2,
            }}>
            {isEditMode && (
                <CloseIcon
                    fontSize="medium"
                    onClick={() => onDelete && onDelete(project)}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        color: theme[themeStyle].true_white,
                        width: 32,
                        height: 32,
                        zIndex: 20,
                        cursor: 'pointer',
                        borderRadius: '50%',
                        p: 0.5,
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.9)' },
                    }}
                />
            )}

            {canPlayInline && (
                <Box
                    component="video"
                    ref={videoRef}
                    playsInline
                    controls={false}
                    poster={project.feature_image}
                    onPlay={handleVideoPlay}
                    onPause={handleVideoPause}
                    onEnded={handleVideoEnded}
                    onClick={handleVideoSurfaceClick}
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        objectPosition: 'center',
                        zIndex: 1,
                        opacity: hasStarted ? 1 : 0,
                        bgcolor: '#0a0a0a',
                        transition: 'opacity 0.35s ease',
                        cursor: hasStarted ? 'pointer' : 'default',
                    }}
                />
            )}

            {(!hasStarted || !canPlayInline) && (
                <CardMedia
                    component="img"
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        objectPosition: 'center',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        zIndex: 2,
                        bgcolor: '#0a0a0a',
                        opacity: hasStarted && canPlayInline ? 0 : 0.85,
                        transition: 'opacity 0.35s ease',
                        pointerEvents: 'none',
                    }}
                    image={project.feature_image}
                    alt={project.title}
                />
            )}

            <Box
                sx={{
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                    width: '100%',
                    height: '42%',
                    zIndex: 3,
                    pointerEvents: 'none',
                    background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 100%)',
                }}
            />

            {showCenterPlay && (
                <IconButton
                    onClick={handleCenterPlay}
                    aria-label="Play video"
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 5,
                        width: playCircleSize,
                        height: playCircleSize,
                        p: 0,
                        backgroundColor: theme[themeStyle].highlight,
                        color: iconOnHighlight,
                        boxShadow: `0 12px 40px ${theme[themeStyle].highlight}55`,
                        '&:hover': {
                            backgroundColor: theme[themeStyle].primary_orange,
                            transform: 'translate(-50%, -50%) scale(1.06)',
                        },
                    }}>
                    <PlayArrowIcon sx={{ fontSize: featured ? 44 : 36, ml: 0.5 }} />
                </IconButton>
            )}

            <Box
                sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                }}>
                <Box
                    sx={{
                        px: { xs: 1.5, sm: 2 },
                        pb: showControlBar ? 1 : 1.5,
                        pt: 0.5,
                        pointerEvents: 'none',
                    }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: featured ? { xs: 18, sm: 22 } : { xs: 15, sm: 16 },
                            color: theme[themeStyle].primary_white,
                            textShadow: '0 2px 12px rgba(0,0,0,0.75)',
                            mb: 0.25,
                        }}>
                        {project.title}
                    </Typography>
                    {!!project.description?.trim() && (
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                fontSize: { xs: 12, sm: 13 },
                                color: 'rgba(255,255,255,0.72)',
                                textShadow: '0 1px 8px rgba(0,0,0,0.65)',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}>
                            {project.description}
                        </Typography>
                    )}
                </Box>

                {showControlBar && (
                    <Box
                        onClick={e => e.stopPropagation()}
                        sx={{
                            flexShrink: 0,
                            pointerEvents: 'auto',
                            bgcolor: theme[themeStyle].secondaryBg,
                            borderTop: '1px solid rgba(255,255,255,0.06)',
                            px: { xs: 1.25, sm: 2 },
                            py: { xs: 1, sm: 1.25 },
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 1, sm: 1.5 },
                            minHeight: 48,
                        }}>
                        {canPlayInline ? (
                            <>
                                <Box sx={{ width: 36, display: 'flex', justifyContent: 'center' }}>
                                    {hasStarted && isPlaying && (
                                        <IconButton
                                            size="small"
                                            onClick={handleTogglePause}
                                            aria-label="Pause"
                                            sx={{
                                                color: 'rgba(255,255,255,0.75)',
                                                '&:hover': { color: theme[themeStyle].highlight },
                                            }}>
                                            <PauseIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                                <Slider
                                    size="small"
                                    value={progressDisplay}
                                    min={0}
                                    max={progressMax}
                                    step={0.1}
                                    onChange={handleScrubChange}
                                    onChangeCommitted={handleScrubCommit}
                                    sx={{
                                        flex: 1,
                                        mx: 0.5,
                                        color: theme[themeStyle].highlight,
                                        height: 6,
                                        '& .MuiSlider-thumb': {
                                            width: 10,
                                            height: 10,
                                            '&:hover, &.Mui-focusVisible': { boxShadow: 'none' },
                                        },
                                        '& .MuiSlider-rail': {
                                            opacity: 1,
                                            bgcolor: 'rgba(255,255,255,0.12)',
                                        },
                                        '& .MuiSlider-track': {
                                            border: 'none',
                                        },
                                    }}
                                />
                                <Typography
                                    component="span"
                                    sx={{
                                        fontFamily: 'ui-monospace, monospace',
                                        fontSize: 11,
                                        color: 'rgba(255,255,255,0.55)',
                                        whiteSpace: 'nowrap',
                                        minWidth: { xs: 72, sm: 88 },
                                        textAlign: 'right',
                                    }}>
                                    {formatMediaTime(progressDisplay)} / {formatMediaTime(duration)}
                                </Typography>
                                <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
                                    <IconButton
                                        size="small"
                                        onClick={handleMuteToggle}
                                        aria-label={isMuted ? 'Unmute' : 'Mute'}
                                        sx={{
                                            color: 'rgba(255,255,255,0.55)',
                                            '&:hover': { color: theme[themeStyle].highlight },
                                        }}>
                                        {isMuted || volumeUi === 0 ? (
                                            <VolumeOffIcon sx={{ fontSize: 20 }} />
                                        ) : (
                                            <VolumeUpIcon sx={{ fontSize: 20 }} />
                                        )}
                                    </IconButton>
                                    <Slider
                                        size="small"
                                        value={volumeSliderValue}
                                        min={0}
                                        max={100}
                                        onChange={handleVolumeChange}
                                        sx={{
                                            width: { xs: 56, sm: 72 },
                                            color: theme[themeStyle].highlight,
                                            height: 4,
                                            '& .MuiSlider-thumb': { width: 8, height: 8 },
                                            '& .MuiSlider-rail': { opacity: 1, bgcolor: 'rgba(255,255,255,0.12)' },
                                        }}
                                    />
                                </Stack>
                            </>
                        ) : (
                            <Box sx={{ flex: 1 }} />
                        )}
                        {expandHandler && (
                            <IconButton
                                size="small"
                                onClick={handleExpand}
                                aria-label="Open full player"
                                sx={{
                                    color: 'rgba(255,255,255,0.75)',
                                    flexShrink: 0,
                                    '&:hover': { color: theme[themeStyle].highlight },
                                }}>
                                <FullscreenIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export { DemoReelCard };
